import { useState } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ObservedProgression } from "@/lib/progression";
import ProgressionArtifact from "./ProgressionArtifact";

interface Props {
  progression: ObservedProgression;
}

/**
 * Cinematic vertical progression sequence.
 * Restrained typography, archival glow, staggered fade-in.
 * Not a chart, not analytics — classified-archive aesthetic.
 */
const ObservedProgression = ({ progression }: Props) => {
  const [exportOpen, setExportOpen] = useState(false);
  const { steps } = progression;

  return (
    <section className="space-y-5">
      {/* Section header — restrained */}
      <div className="text-center space-y-1.5">
        <p className="text-[10px] font-mono tracking-[0.32em] uppercase text-muted-foreground/75">
          Observed Progression
        </p>
        <p className="text-[11px] text-muted-foreground/45 italic font-light">
          Patterns observed during conversational escalation.
        </p>
      </div>

      {/* The sequence — vertical, centered, cinematic */}
      <div className="relative rounded-xl border border-border/40 bg-card/20 backdrop-blur-sm py-10 px-6">
        {/* faint archival corner marks */}
        <div aria-hidden className="absolute top-2 left-2 w-3 h-3 border-l border-t border-cyan/20" />
        <div aria-hidden className="absolute top-2 right-2 w-3 h-3 border-r border-t border-cyan/20" />
        <div aria-hidden className="absolute bottom-2 left-2 w-3 h-3 border-l border-b border-cyan/20" />
        <div aria-hidden className="absolute bottom-2 right-2 w-3 h-3 border-r border-b border-cyan/20" />

        <ol className="flex flex-col items-center gap-0">
          {steps.map((step, i) => (
            <li
              key={`${step.stage}-${i}`}
              className="flex flex-col items-center"
              style={{
                animation: `progressionReveal 700ms ease-out both`,
                animationDelay: `${i * 220}ms`,
              }}
            >
              <p className="text-base md:text-lg font-light tracking-[0.22em] uppercase text-foreground/85 text-center">
                {step.stage}
              </p>

              {i < steps.length - 1 && (
                <div
                  className="my-5 flex flex-col items-center gap-1.5"
                  style={{
                    animation: `progressionArrow 900ms ease-out both`,
                    animationDelay: `${i * 220 + 180}ms`,
                  }}
                >
                  <div className="h-6 w-px bg-gradient-to-b from-cyan/30 to-cyan/5" />
                  <span className="text-cyan/45 text-[11px] leading-none">↓</span>
                </div>
              )}
            </li>
          ))}
        </ol>
      </div>

      {/* Footer micro-copy */}
      <p className="text-[9px] font-mono tracking-[0.28em] uppercase text-muted-foreground/35 text-center">
        Behavioral sequence · Observational, not predictive
      </p>

      {/* Preserve action */}
      <div className="flex justify-center pt-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExportOpen(true)}
          className="text-xs tracking-[0.18em] uppercase font-mono text-muted-foreground/70 hover:text-foreground"
        >
          <Sparkles className="w-3.5 h-3.5 mr-2 opacity-60" />
          Preserve Progression
        </Button>
      </div>

      <ProgressionArtifact
        progression={progression}
        open={exportOpen}
        onOpenChange={setExportOpen}
      />

      <style>{`
        @keyframes progressionReveal {
          0%   { opacity: 0; transform: translateY(8px); letter-spacing: 0.32em; }
          100% { opacity: 1; transform: translateY(0); letter-spacing: 0.22em; }
        }
        @keyframes progressionArrow {
          0%   { opacity: 0; transform: scaleY(0.4); }
          100% { opacity: 1; transform: scaleY(1); }
        }
      `}</style>
    </section>
  );
};

export default ObservedProgression;
